import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { X, Loader2, BookOpen, ShieldAlert } from 'lucide-react';
import ReadinessWidget from '../components/payroll-review/ReadinessWidget';
import PayrollQueue from '../components/payroll-review/PayrollQueue';
import LockBar from '../components/payroll-review/LockBar';
import LockHistory from '../components/payroll-review/LockHistory';
import AdjustmentsTab from '../components/payroll-review/AdjustmentsTab';
import OverrideApproveDialog from '../components/payroll-review/OverrideApproveDialog';
import OverridesTab from '../components/payroll-review/OverridesTab';
import { fmtDate, fmtTime } from '../components/approvals/shared';

const API = `${window.__BACKEND_URL__}/api`;

const ymd = (d) => d.toISOString().slice(0, 10);
const STATUS_LABELS = {
  needs_review: 'Not submitted', submitted: 'Awaiting supervisor', resubmitted: 'Awaiting supervisor',
  returned: 'Returned to worker', sup_approved: 'Awaiting payroll', payroll_exception: 'Payroll exception',
  payroll_approved: 'Payroll approved', locked: 'Locked', paid: 'Paid',
};

export default function PayrollReview() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState('review');
  const [override, setOverride] = useState(null); // { ids: [], label }
  const [payPeriod, setPayPeriod] = useState(null);
  const [periodKey, setPeriodKey] = useState('current');
  const [period, setPeriod] = useState({ start: ymd(new Date(Date.now() - 13 * 86400000)), end: ymd(new Date()) });
  const [summary, setSummary] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillEntries, setDrillEntries] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdminUser = user?.role === 'admin' || user?.is_super_user;

  useEffect(() => {
    if (!token || !isAdminUser) return;
    axios.get(`${API}/company/pay-period`, auth)
      .then(({ data }) => {
        setPayPeriod(data);
        if (data.configured && data.current) {
          setPeriod({ start: data.current.start, end: data.current.end });
        }
      })
      .catch(() => setPayPeriod({ configured: false }));
  }, [token]); // eslint-disable-line

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payroll/readiness`, {
        ...auth, params: { start_date: period.start, end_date: period.end } });
      setSummary(data);
    } catch (e) { /* widget just won't show */ }
  }, [token, period.start, period.end]); // eslint-disable-line

  useEffect(() => { if (token && isAdminUser) loadSummary(); }, [token, isAdminUser, loadSummary, refreshKey]);

  const onDrillDown = async (statusKey, label) => {
    setDrill({ key: statusKey, label });
    setDrillEntries(null);
    try {
      const { data } = await axios.get(`${API}/payroll/period-entries`, {
        ...auth, params: { status: statusKey, start_date: period.start, end_date: period.end } });
      setDrillEntries(data.entries || []);
    } catch (e) { setDrillEntries([]); }
  };

  const pickPeriod = (key) => {
    setPeriodKey(key);
    setDrill(null);
    if (key === 'current' && payPeriod?.current) setPeriod({ start: payPeriod.current.start, end: payPeriod.current.end });
    else if (key === 'previous' && payPeriod?.previous) setPeriod({ start: payPeriod.previous.start, end: payPeriod.previous.end });
    else if (key === 'current' && !payPeriod?.configured) setPeriod({ start: ymd(new Date(Date.now() - 13 * 86400000)), end: ymd(new Date()) });
  };

  if (user && user.role !== 'admin' && !user.is_super_user) {
    return <Navigate to="/home" replace />;
  }

  const onChanged = () => { setRefreshKey((k) => k + 1); if (drill) onDrillDown(drill.key, drill.label); };
  const inputStyle = { background: '#1A2433', color: '#fff', border: '1px solid #2D3748' };

  return (
    <div className="p-4 md:p-6 space-y-5 pb-2" data-testid="payroll-review-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">Payroll Review</h1>
          <p className="text-sm mt-0.5" style={{ color: '#A0AEC0' }}>
            Final payroll approval and pay-period lock. Push to QuickBooks from the Process &amp; Push tab.
          </p>
        </div>
        <button onClick={() => navigate('/user-manual?tab=payroll')}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
          style={{ background: '#1A2433', color: '#93C5FD', border: '1px solid #2D3748' }}
          data-testid="payroll-guide-link">
          <BookOpen className="w-4 h-4" /> Payroll &amp; timekeeping guide
        </button>
      </div>

      <ReadinessWidget summary={summary} onDrillDown={onDrillDown} />

      {/* Pay period selector */}
      <div className="ht-card p-3 flex flex-wrap items-center gap-2" data-testid="pay-period-selector">
        {[['current', 'Current Period'], ['previous', 'Previous Period'], ['custom', 'Custom']].map(([k, l]) => (
          <button key={k} onClick={() => pickPeriod(k)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{
              background: periodKey === k ? '#1E88E5' : 'transparent',
              color: periodKey === k ? '#fff' : '#A0AEC0',
              border: `1px solid ${periodKey === k ? '#1E88E5' : '#2D3748'}`,
            }}
            data-testid={`period-preset-${k}`}>
            {l}
          </button>
        ))}
        {periodKey === 'custom' && (
          <div className="flex items-center gap-2 ml-1">
            <input type="date" value={period.start} onChange={(e) => setPeriod({ ...period, start: e.target.value })}
              className="px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="period-custom-start" />
            <span style={{ color: '#64748B' }}>→</span>
            <input type="date" value={period.end} onChange={(e) => setPeriod({ ...period, end: e.target.value })}
              className="px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="period-custom-end" />
          </div>
        )}
        <span className="ml-auto text-xs" style={{ color: '#64748B' }} data-testid="period-range-label">
          {period.start} → {period.end}
          {payPeriod && !payPeriod.configured && ' · no pay-period cycle configured (using last 14 days)'}
        </span>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2" data-testid="payroll-review-tabs">
        {[['review', 'Payroll Review'], ['adjustments', 'Payroll Adjustments'], ['overrides', 'Overrides']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className="px-3.5 py-2 rounded-xl text-sm font-medium transition-colors"
            style={{
              background: tab === k ? '#1E88E5' : '#1A2433',
              color: tab === k ? '#fff' : '#A0AEC0',
              border: `1px solid ${tab === k ? '#1E88E5' : '#2D3748'}`,
            }}
            data-testid={`payroll-tab-${k}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'review' && (
        <>
          {/* Blocking drill-down list */}
          {drill && (
            <div className="ht-card p-4" data-testid="blocking-drilldown">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <p className="text-sm font-semibold text-white">
                  Blocking: {drill.label} {drillEntries && <span style={{ color: '#FBBF24' }}>({drillEntries.length})</span>}
                </p>
                <div className="flex items-center gap-2">
                  {drillEntries && drillEntries.length > 0 && (
                    <button
                      onClick={() => setOverride({ ids: drillEntries.map((e) => e.id), label: drill.label })}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'rgba(245,158,11,0.15)', color: '#FBBF24', border: '1px solid rgba(245,158,11,0.4)' }}
                      title="Admin override: approve every shift shown for payroll without worker certification / supervisor review (reason required, audit-logged)"
                      data-testid="override-approve-all-btn">
                      <ShieldAlert className="w-3.5 h-3.5" /> Approve all shown (override)
                    </button>
                  )}
                  <button onClick={() => setDrill(null)} className="p-1.5 rounded-lg" style={{ color: '#A0AEC0' }} data-testid="drilldown-clear-btn">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {drillEntries === null ? (
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#1E88E5' }} />
              ) : drillEntries.length === 0 ? (
                <p className="text-xs" style={{ color: '#64748B' }}>No entries in this category anymore.</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {drillEntries.map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                      style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                      data-testid={`drilldown-entry-${e.id}`}>
                      <div className="min-w-0">
                        <p className="text-xs text-white font-semibold">{e.user_name || 'Unknown'} · {fmtDate(e.clock_in)}</p>
                        <p className="text-[11px]" style={{ color: '#A0AEC0' }}>
                          {fmtTime(e.clock_in)} → {e.clock_out ? fmtTime(e.clock_out) : '—'}
                          {e.returned_reason && <span style={{ color: '#FB923C' }}> · "{e.returned_reason}"</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
                          style={{ background: 'rgba(255,160,0,0.15)', color: '#FBBF24' }}>
                          {STATUS_LABELS[e.approval_status] || STATUS_LABELS.needs_review}
                        </span>
                        <button
                          onClick={() => setOverride({ ids: [e.id], label: `${e.user_name || 'worker'} · ${fmtDate(e.clock_in)}` })}
                          className="text-[11px] px-2 py-1 rounded-lg font-semibold whitespace-nowrap"
                          style={{ background: 'rgba(245,158,11,0.15)', color: '#FBBF24', border: '1px solid rgba(245,158,11,0.4)' }}
                          data-testid={`override-approve-btn-${e.id}`}>
                          Approve (override)
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <OverrideApproveDialog
            open={!!override}
            entryIds={override?.ids || []}
            label={override?.label || ''}
            token={token}
            onClose={() => setOverride(null)}
            onDone={onChanged}
          />

          <PayrollQueue token={token} period={period} onChanged={onChanged} refreshKey={refreshKey} />
          <LockHistory token={token} period={period} refreshKey={refreshKey} />
          <LockBar token={token} period={period} readinessPct={summary?.readiness_pct ?? 0} onChanged={onChanged} onOpenPush={() => navigate(`/payroll?tab=process&start=${period.start}&end=${period.end}`)} />
        </>
      )}

      {tab === 'adjustments' && <AdjustmentsTab token={token} currentUserId={user?.id} />}
      {tab === 'overrides' && <OverridesTab token={token} period={period} />}
    </div>
  );
}
